import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { deleteProject, exportPromptAsSDK } from '../../services/api';
import { getModelDisplayName } from '../../constants/models';
import { highlightMarkdown } from '../common/MarkdownHighlighter';
import { useModal } from '../../contexts/ModalContext';

const ProjectDetail = ({ project, onBack, onDelete, isStreaming, streamContent, currentStreamLabel }) => {
  const { confirmDelete } = useModal();
  const [activeTab, setActiveTab] = useState(isStreaming ? 'output' : 'input');
  const [outputTab, setOutputTab] = useState('system');
  const outputRef = useRef(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (isStreaming && currentStreamLabel) {
      setOutputTab(currentStreamLabel);
    }
  }, [isStreaming, currentStreamLabel]);

  useEffect(() => {
    if (isStreaming && outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [isStreaming, streamContent]);

  const handleCopy = useCallback(async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied!`);
    } catch (error) {
      toast.error('Failed to copy');
    }
  }, []);

  const handleExport = useCallback(async (sdkType) => {
    if (!project.id) {
      toast.error('Project ID not available');
      return;
    }

    setIsExporting(true);
    try {
      const response = await exportPromptAsSDK(project.id, sdkType);
      
      if (response.success) {
        const url = URL.createObjectURL(response.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(`Exported as ${sdkType}!`);
        setShowExportModal(false);
      } else {
        toast.error(response.error || 'Export failed');
      }
    } catch (error) {
      toast.error('Failed to export prompt');
    } finally {
      setIsExporting(false);
    }
  }, [project.id]);

  const handleDelete = useCallback(async () => {
    const confirmed = await confirmDelete(project.project_name);
    if (!confirmed) {
      return;
    }

    try {
      const result = await deleteProject(project.id);
      if (result.success) {
        toast.success('Project deleted successfully!');
        if (onDelete) {
          onDelete(project.id);
        }
        onBack();
      } else {
        toast.error(result.error || 'Failed to delete project');
      }
    } catch (error) {
      toast.error('Failed to delete project');
    }
  }, [project, onBack, onDelete, confirmDelete]);

  const promptType = project.user_input?.type || 'System User';
  const outputTabs = [];
  
  if (isStreaming) {
    // During streaming, show tabs based on prompt type
    if (promptType !== 'User Only' && streamContent?.system !== undefined) {
      outputTabs.push({ id: 'system', label: 'System Prompt' });
    }
    outputTabs.push({ id: 'user', label: 'User Prompt' });
    if (promptType === 'System  User  Assistant ' && streamContent?.examples !== undefined) {
      outputTabs.push({ id: 'examples', label: 'Few-Shot Examples' });
    }
  } else {
    // Show tabs based on prompt type and available content
    if (promptType === 'User Only') {
      // User Only: only user prompt
      if (project.user_prompt_output) outputTabs.push({ id: 'user', label: 'User Prompt' });
    } else if (promptType === 'System User') {
      // System User: system + user prompts
      if (project.system_prompt_output) outputTabs.push({ id: 'system', label: 'System Prompt' });
      if (project.user_prompt_output) outputTabs.push({ id: 'user', label: 'User Prompt' });
    } else if (promptType === 'System  User  Assistant ') {
      // Few-shot: system + user + examples
      if (project.system_prompt_output) outputTabs.push({ id: 'system', label: 'System Prompt' });
      if (project.user_prompt_output) outputTabs.push({ id: 'user', label: 'User Prompt' });
      if (project.few_shot_examples) outputTabs.push({ id: 'examples', label: 'Few-Shot Examples' });
    }
  }
  
  // Set default tab based on prompt type when project changes
  useEffect(() => {
    if (!isStreaming) {
      if (promptType === 'User Only') {
        setOutputTab('user');
      } else {
        setOutputTab('system');
      }
    }
  }, [project.id, promptType, isStreaming]);

  const getOutputContent = () => {
    if (isStreaming && streamContent) {
      // Return streaming content with a cursor indicator
      switch (outputTab) {
        case 'system': return (streamContent.system || '') + (streamContent.system ? '▌' : 'Generating...');
        case 'user': return (streamContent.user || '') + (streamContent.user ? '▌' : 'Waiting...');
        case 'examples': return (streamContent.examples || '') + (streamContent.examples ? '▌' : 'Waiting...');
        default: return '';
      }
    }
    
    switch (outputTab) {
      case 'system': return project.system_prompt_output || 'No system prompt';
      case 'user': return project.user_prompt_output || 'No user prompt';
      case 'examples': return project.few_shot_examples || 'No examples';
      default: return '';
    }
  };

  return (
    <>
      <div className="h-full flex flex-col bg-bg-primary overflow-hidden">
        {/* Header with back button and project info */}
        <div className="pd-header">
        <button onClick={onBack} className="btn-back" disabled={isStreaming}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back to Prompts
        </button>
          
          <div className="pd-header-content">
            <div className="pd-title-row">
              <h2 className="pd-title">{project.project_name || 'Untitled Project'}</h2>
            {isStreaming && <span className="streaming-badge">Generating...</span>}
            </div>
          {!isStreaming && (
              <div className="pd-metadata">
              {project.created_at && (
                  <span className="pd-meta-item">
                    <span className="pd-meta-label">Created:</span>
                    <span className="pd-meta-value">{new Date(project.created_at).toLocaleString()}</span>
            </span>
              )}
              {project.commit_id && (
                  <span className="pd-meta-item">
                    <span className="pd-meta-label">Version:</span>
                    <code className="pd-commit-id">{project.commit_id}</code>
                </span>
              )}
            </div>
          )}
        </div>

        {!isStreaming && (
            <div className="pd-actions">
              <button onClick={() => setShowExportModal(true)} className="btn-secondary">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
              Export
            </button>
            {project.permission === 'owner' && (
            <button onClick={handleDelete} className="btn-delete">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
              Delete
            </button>
            )}
          </div>
        )}
      </div>

      {/* Main tabs */}
      <div className="pd-tabs">
        <button className={`pd-tab ${activeTab === 'input' ? 'active' : ''}`} onClick={() => setActiveTab('input')}>
          Input Configuration
        </button>
        <button className={`pd-tab ${activeTab === 'output' ? 'active' : ''}`} onClick={() => setActiveTab('output')}>
          Generated Prompt
        </button>
      </div>

      {/* Content area */}
      <div className="pd-content">
        {activeTab === 'input' ? (
          <div className="pd-input-section">
            <div className="pd-card">
              <h3 className="pd-section-title">Description</h3>
              <div className="pd-card-value">
                {project.user_input?.description || 'No description'}
              </div>
            </div>

            <div className="pd-card">
              <h3 className="pd-section-title">Use Case</h3>
              <div className="pd-card-value">
                {project.user_input?.use_case || 'No use case'}
              </div>
            </div>

            <div className="pd-grid">
              <div className="pd-card">
                <h3 className="pd-section-title">Prompt Type</h3>
                <div className="pd-badge">{project.user_input?.type || 'System User'}</div>
              </div>
              
              <div className="pd-card">
                <h3 className="pd-section-title">Output Format</h3>
                <div className="pd-badge">
                  {project.user_input?.output_type === 'Schema' ? 'JSON Schema' : (project.user_input?.output_type || 'Plain Text')}
                </div>
              </div>
              
              <div className="pd-card">
                <h3 className="pd-section-title">Model</h3>
                <div className="pd-badge">{getModelDisplayName(project.model)}</div>
              </div>
            </div>

            {project.user_input?.input_variables?.length > 0 && (
              <div className="pd-card">
                <h3 className="pd-section-title">Variables</h3>
                <div className="pd-variables">
                  {project.user_input.input_variables.map((v, i) => (
                    <div key={i} className="pd-variable-row">
                      <span className="pd-var-name">{v.name}</span>
                      <span className="pd-var-desc">{v.description}</span>
                      {v.type && <span className="pd-var-type">{v.type}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {project.user_input?.additional_input_variables?.length > 0 && (
              <div className="pd-card">
                <h3 className="pd-section-title">Optional Variables</h3>
                <div className="pd-variables">
                  {project.user_input.additional_input_variables.map((v, i) => (
                    <div key={i} className="pd-variable-row system">
                      <span className="pd-var-name">{v.name}</span>
                      <span className="pd-var-desc">{v.description}</span>
                      <span className="pd-var-type">{v.type}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {project.user_input?.additional_config && (
              project.user_input.additional_config.output_constraints || 
              project.user_input.additional_config.must_include || 
              project.user_input.additional_config.must_avoid || 
              project.user_input.additional_config.special_instructions
            ) && (
              <div className="pd-card">
                <h3 className="pd-section-title">Additional Configuration</h3>
                <div className="pd-additional-config">
                  {project.user_input.additional_config.output_constraints && (
                    <div className="pd-config-item">
                      <span className="pd-config-label">Output Constraints:</span>
                      <span className="pd-config-value">{project.user_input.additional_config.output_constraints}</span>
                    </div>
                  )}
                  {project.user_input.additional_config.must_include && (
                    <div className="pd-config-item">
                      <span className="pd-config-label">Must Include:</span>
                      <span className="pd-config-value">{project.user_input.additional_config.must_include}</span>
                    </div>
                  )}
                  {project.user_input.additional_config.must_avoid && (
                    <div className="pd-config-item">
                      <span className="pd-config-label">Must Avoid:</span>
                      <span className="pd-config-value">{project.user_input.additional_config.must_avoid}</span>
                    </div>
                  )}
                  {project.user_input.additional_config.special_instructions && (
                    <div className="pd-config-item">
                      <span className="pd-config-label">Special Instructions:</span>
                      <span className="pd-config-value">{project.user_input.additional_config.special_instructions}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {project.user_input?.schema && (
              <div className="pd-card">
                <h3 className="pd-section-title">JSON Schema</h3>
                <pre className="pd-schema">
                  {(() => {
                    try {
                      const parsed = typeof project.user_input.schema === 'string' 
                        ? JSON.parse(project.user_input.schema) 
                        : project.user_input.schema;
                      return JSON.stringify(parsed, null, 2);
                    } catch {
                      return project.user_input.schema;
                    }
                  })()}
                </pre>
              </div>
            )}
          </div>
        ) : (
          <div className="prompts-view">
            <div className="output-tabs-container">
              <div className="output-tabs">
                {outputTabs.map((tab) => (
                  <button
                    key={tab.id}
                    className={`output-tab ${outputTab === tab.id ? 'active' : ''}`}
                    onClick={() => setOutputTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
                <button onClick={() => handleCopy(getOutputContent(), 'Content')} className="btn-copy">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                  Copy
                </button>
            </div>

            <div className="output-panel">
            <pre ref={outputRef} className="panel-content md-highlighted">
                {highlightMarkdown(getOutputContent())}
              </pre>
            </div>
          </div>
        )}
      </div>

      {showExportModal && (
        <div className="modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="modal-content export-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Export Prompt</h3>
              <button onClick={() => setShowExportModal(false)} className="modal-close">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-description">Choose your export format:</p>
              <div className="export-options">
                <button 
                  onClick={() => handleExport('markdown')} 
                  className="export-option"
                  disabled={isExporting}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                  </svg>
                  <span>Markdown</span>
                  <small>Documentation format</small>
                </button>
                <button 
                  onClick={() => handleExport('langchain')} 
                  className="export-option"
                  disabled={isExporting}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                  </svg>
                  <span>LangChain</span>
                  <small>Python SDK</small>
                </button>
                <button 
                  onClick={() => handleExport('openai')} 
                  className="export-option"
                  disabled={isExporting}
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M22.282 9.821a5.985 5.985 0 00-.516-4.91 6.046 6.046 0 00-6.51-2.9A6.065 6.065 0 004.981 4.18a5.985 5.985 0 00-3.998 2.9 6.046 6.046 0 00.743 7.097 5.98 5.98 0 00.51 4.911 6.051 6.051 0 006.515 2.9A5.985 5.985 0 0013.26 24a6.056 6.056 0 005.772-4.206 5.99 5.99 0 003.997-2.9 6.056 6.056 0 00-.747-7.073zM13.26 22.43a4.476 4.476 0 01-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 00.392-.681v-6.737l2.02 1.168a.071.071 0 01.038.052v5.583a4.504 4.504 0 01-4.494 4.494zM3.6 18.304a4.47 4.47 0 01-.535-3.014l.142.085 4.783 2.759a.771.771 0 00.78 0l5.843-3.369v2.332a.08.08 0 01-.033.062L9.74 19.95a4.5 4.5 0 01-6.14-1.646zM2.34 7.896a4.485 4.485 0 012.366-1.973V11.6a.766.766 0 00.388.676l5.815 3.355-2.02 1.168a.076.076 0 01-.071 0l-4.83-2.786A4.504 4.504 0 012.34 7.896zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 01.071 0l4.83 2.791a4.494 4.494 0 01-.676 8.105v-5.678a.79.79 0 00-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 00-.785 0L9.409 9.23V6.897a.066.066 0 01.028-.061l4.83-2.787a4.5 4.5 0 016.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 01-.038-.057V6.075a4.5 4.5 0 017.375-3.453l-.142.08L8.704 5.46a.795.795 0 00-.393.681v6.733zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5v-3z" fill="currentColor"/>
                  </svg>
                  <span>OpenAI</span>
                  <small>Python SDK</small>
                </button>
                <button 
                  onClick={() => handleExport('anthropic')} 
                  className="export-option"
                  disabled={isExporting}
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M14.5 2L22 22h-4L14 14 10 22H6l7.5-20h1z" fill="currentColor"/>
                    <path d="M9.5 2L2 22h4l4-8 4 8h4L10.5 2h-1z" fill="currentColor" opacity="0.6"/>
                  </svg>
                  <span>Anthropic Claude</span>
                  <small>Python SDK</small>
                </button>
                <button 
                  onClick={() => handleExport('litellm')} 
                  className="export-option"
                  disabled={isExporting}
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect x="4" y="4" width="7" height="7" rx="1" fill="currentColor"/>
                    <rect x="13" y="4" width="7" height="7" rx="1" fill="currentColor" opacity="0.7"/>
                    <rect x="4" y="13" width="7" height="7" rx="1" fill="currentColor" opacity="0.7"/>
                    <rect x="13" y="13" width="7" height="7" rx="1" fill="currentColor" opacity="0.5"/>
                  </svg>
                  <span>LiteLLM</span>
                  <small>Universal SDK</small>
                </button>
                <button 
                  onClick={() => handleExport('curl')} 
                  className="export-option"
                  disabled={isExporting}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="4 17 10 11 4 5"></polyline>
                    <line x1="12" y1="19" x2="20" y2="19"></line>
                  </svg>
                  <span>cURL</span>
                  <small>Shell script</small>
                </button>
              </div>
            </div>
            <div className="modal-actions">
              <button 
                onClick={() => setShowExportModal(false)} 
                className="btn-secondary"
                disabled={isExporting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
    </>
  );
};

ProjectDetail.propTypes = {
  project: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  isStreaming: PropTypes.bool,
  streamContent: PropTypes.shape({
    system: PropTypes.string,
    user: PropTypes.string,
    examples: PropTypes.string,
  }),
  currentStreamLabel: PropTypes.string,
};

ProjectDetail.defaultProps = {
  onDelete: null,
  isStreaming: false,
  streamContent: null,
  currentStreamLabel: 'system',
};

export default ProjectDetail;

